import { ChangeEvent, ReactNode, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import Flex from "./Flex";
import Caption from "./Caption";
import DialogActions from "@mui/material/DialogActions";
import LoadingSpinner from "./LoadingSpinner";
import GetAppIcon from "@mui/icons-material/GetApp";
import { useAppSelector } from "redux/hooks";
import { selectClientLensExportConfigs } from "redux/slices/clientLens";
import theme from "theme";
import { selectCollectEnabled } from "redux/slices/session";
import { useExportFunctions } from "helpers/hooks/useExportFunctions";
import DescriptionTooltip from "./FormField/DescriptionTooltip";

const ENCRYPTION_WARNING =
  "Due to the sensitive nature of the contained information, it is recommended to encrypt " +
  "PDF Exports using a password of 4 or more characters. If you do not wish to encrypt the " +
  "exported document, un-check the box below.";

export interface ExportModalProps {
  body: ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  caseId: number;
}

const ExportModal = (props: ExportModalProps) => {
  const { body, setOpen, caseId, open } = props;
  const collectEnabled = useAppSelector(selectCollectEnabled);

  const [exporting, setExporting] = useState(false);
  const [password, setPassword] = useState<string>("");
  const [encrypt, setEncrypt] = useState(false);
  const [configId, setConfigId] = useState<number>(-1);
  const [shareToCollect, setShareToCollect] = useState(false);
  const [pushToTransfer, setPushToTransfer] = useState(false);
  const [fullRecordSetExport, setFullRecordSetExport] = useState(false);
  const [customTransferFolder, setCustomTransferFolder] = useState<string>("");
  const profileExportConfigs = useAppSelector(selectClientLensExportConfigs);
  const { doCaseExport, doFullRecordSetExport } = useExportFunctions();

  const needPassword = encrypt && password.length < 4;

  const doExport = fullRecordSetExport ? doFullRecordSetExport : doCaseExport;

  const handleExport = async () => {
    setExporting(true);
    const passwordToUse = encrypt ? password : undefined;

    try {
      await doExport(caseId, {
        collectShared: shareToCollect,
        pushToTransfer: pushToTransfer,
        customTransferFolder: customTransferFolder || undefined,
        password: passwordToUse,
        configId: configId !== -1 ? configId : undefined,
      });
      setOpen(false);
    } finally {
      setExporting(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogContent
        sx={{
          minWidth: "600px",
          minHeight: "450px",
        }}>
        {body}
        <FormControl sx={{ width: "300px", mt: 2 }}>
          <InputLabel>Config</InputLabel>
          <Select
            value={configId}
            size="small"
            label="Config"
            onChange={(event) => setConfigId(event.target.value as number)}
            displayEmpty>
            <MenuItem value={-1}>Standard</MenuItem>
            {profileExportConfigs.map((pec) => (
              <MenuItem key={pec.id} value={pec.id}>
                {pec.name}
              </MenuItem>
            ))}
          </Select>

          <div className="flex items-center gap-2">
            <FormControlLabel
              className="mr-0"
              label="Full Record Set Export"
              control={
                <Checkbox
                  checked={fullRecordSetExport}
                  onChange={(evt) => setFullRecordSetExport(evt.target.checked)}
                />
              }
            />
            <DescriptionTooltip description="Download a ZIP file, with one PDF per document" />
          </div>

          <FormControlLabel
            label="Share to Collect"
            disabled={!collectEnabled}
            control={<Checkbox checked={shareToCollect} onChange={(evt) => setShareToCollect(evt.target.checked)} />}
          />
          <FormControlLabel
            label="Send to SFTP"
            control={<Checkbox checked={pushToTransfer} onChange={(evt) => setPushToTransfer(evt.target.checked)} />}
          />
          {pushToTransfer && (
            <TextField
              id="custom-transfer-folder"
              label="Custom Transfer Folder"
              value={customTransferFolder}
              onChange={(event: ChangeEvent<any>) => {
                setCustomTransferFolder(event.target.value);
              }}
            />
          )}
        </FormControl>
        <Divider
          sx={{
            margin: theme.spacing(2, 0),
          }}
        />

        <Caption text={ENCRYPTION_WARNING} />
        <Flex p={2}>
          <Flex flexDirection="row">
            <FormControlLabel
              label="Encrypt"
              control={<Checkbox checked={encrypt} onChange={(evt) => setEncrypt(evt.target.checked)} />}
            />
            <TextField
              style={{ display: encrypt ? undefined : "none" }}
              label="Password"
              name="password"
              onChange={(evt) => setPassword(evt.target.value)}
              value={password}
              type="password"
            />
          </Flex>
        </Flex>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="outlined"
          color="default"
          disabled={needPassword || exporting}
          startIcon={exporting ? <LoadingSpinner size={24} /> : <GetAppIcon />}>
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportModal;
